import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  type ComboboxProps,
} from "@headlessui/react";
import { TournamentPlayer } from "@mixitone/models";
import ApplicationController, { ApplicationView } from "@mixitone/mvc";
import clsx from "clsx";

interface Props {
  autoFocus?: boolean;
  onSelect(player: TournamentPlayer): void;
  onRegister?(value: string): void;
  playerStatus?(player: TournamentPlayer): string | undefined;
  players: TournamentPlayer[];
  value: TournamentPlayer | null | "new";
  tournamentId: string;
  error?: string;
  disabled?: boolean;
}

interface State {
  players: TournamentPlayer[];
  filteredPlayers: TournamentPlayer[];
  search: string;
  allowRegister: boolean;
  onRegister(value: string): void;
  onSelect(player: TournamentPlayer): void;
}

class PlayerSearchController extends ApplicationController<State, Props> {
  get initialState(): State {
    return {
      players: [],
      filteredPlayers: [],
      search: "",
      allowRegister: false,
      onRegister: () => {},
      onSelect: () => {},
    };
  }

  async initialize(props: Props) {
    console.log("initialize PlayerSearchController", props);
    this.changeProps(props);
  }

  async changeProps(props: Props) {
    if (!props.players) throw new Error("players is required");
    this.state.players = props.players;
    this.state.allowRegister = typeof props.onRegister === "function";
    if (props.onRegister) {
      this.state.onRegister = props.onRegister;
    }
    this.state.onSelect = props.onSelect;
  }

  actionSetSearch(search: string) {
    this.state.search = search;

    if (search.length < 2) {
      this.state.filteredPlayers = [];
      return;
    }

    this.state.filteredPlayers = this.state.players.filter((player) => {
      return (
        player.name?.toLowerCase().includes(search.toLowerCase()) ||
        player.email?.toLowerCase().includes(search.toLowerCase())
      );
    });
  }

  actionSelect(player: TournamentPlayer) {
    this.state.onSelect(player);
    this.actionSetSearch("");
  }

  actionRegister() {
    if (this.state.search.length > 1) {
      this.state.onRegister(this.state.search);
    }
  }

  get canRegister() {
    return this.state.allowRegister && this.state.search.length > 1;
  }
}

const TournamentPlayerSearch: React.FC<Props> = ({ value, autoFocus, error, disabled, playerStatus }) => {
  const controller = PlayerSearchController.use();
  const { filteredPlayers, search } = controller.state;

  const options = filteredPlayers.map((player, idx) => {
    const status = playerStatus?.(player);
    return (
      <ComboboxOption
        key={player.id}
        value={player}
        // biome-ignore lint/a11y/useValidAriaValues: old
        aria-roledescription=""
        className={clsx(
          "border-l border-r border-slate-300 p-4 ui-active:bg-brand-100 ui-active:text-black ui-not-active:bg-white ui-not-active:text-black",
          { "rounded-b-lg border-b": !controller.canRegister && idx === filteredPlayers.length - 1 },
        )}
      >
        <div className="flex items-center justify-between gap-3">
          <span>{player.name}</span>
          {player.email && <span className="text-sm text-gray-500">{player.email}</span>}
        </div>
        {status && <div className="mt-1 text-sm text-gray-500">{status}</div>}
      </ComboboxOption>
    );
  });

  // Trick to make the combobox accept the union type
  const comboboxProps: ComboboxProps<typeof value, false> = {
    value: value,
    disabled,
  };

  return (
    <Combobox
      {...comboboxProps}
      onChange={(v) => {
        if (v === "new") {
          controller.actionRegister();
        } else if (v instanceof TournamentPlayer) {
          controller.actionSelect(v);
        }
      }}
    >
      <div className="relative w-full">
        <ComboboxInput
          title="Search for a player"
          data-testid="player-search-input"
          data-1p-ignore
          autoFocus={autoFocus}
          autoComplete="off"
          autoCorrect="off"
          placeholder="Search or create player"
          className={clsx(
            "mt-1 block w-full rounded border p-2",
            error ? "border-red-500" : "border-gray-300",
          )}
          onChange={(event) => controller.actionSetSearch(event.target.value)}
          displayValue={(_value: TournamentPlayer) => ""}
        />
        <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none right-3">
          <svg
            aria-hidden="true"
            className="w-5 h-5 text-gray-500 dark:text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>
      {error && <div className="mt-1 text-sm text-red-500">{error}</div>}
      <ComboboxOptions
        anchor="bottom start"
        className="relative z-50"
        style={{ width: "var(--input-width)" }}
      >
        {options}
        {controller.canRegister && (
          <ComboboxOption
            key="new"
            value={"new"}
            disabled={!controller.canRegister}
            className="p-4 border-b border-l border-r rounded-b-lg border-slate-300 ui-active:bg-brand-100 ui-active:text-black ui-not-active:bg-white ui-not-active:text-black"
            onSelect={() => controller.actionRegister()}
          >
            Register {search}
          </ComboboxOption>
        )}
      </ComboboxOptions>
    </Combobox>
  );
};

const PlayerSearchScoped = PlayerSearchController.scope(ApplicationView(TournamentPlayerSearch));
export { PlayerSearchScoped as TournamentPlayerSearch };
